import { getSurveyAnswers } from "src/hooks/survey/answers";
import { getBMI } from "./medEligibility";
import { isFreemiumHealthRoute } from "@utils/userSegment";
import { trackBuyflowEvent } from "./api/tracker";
import saveToBraze from "./brazeUploader";
import {
  answeredYesToMedicareBridgeScreening,
  hasMedicareInsurance,
  isEligibleForBrandedAsync,
} from "./userSegment/features";

const MEDICARE_BRIDGE_DISQUALIFYING_CONDITIONS: string[] = [
  "obstructiveSleepApnea",
  "metabolicDysfunctionAssociatedSteatohepatitis",
];

const MEDICARE_BRIDGE_ELIGIBILITY_TIERS: {
  minBMI: number;
  qualifyingComorbidities?: string[];
}[] = [
  { minBMI: 35 },
  {
    minBMI: 30,
    qualifyingComorbidities: [
      "heartFailureWithPreservedEjectionFraction",
      "uncontrolledHypertension",
      "chronicKidneyDiseaseStage3aOrAbove",
      "preDiabetes",
      "previousMyocardialInfarction",
      "previousStroke",
      "symptomaticPeripheralArteryDisease",
    ],
  },
  {
    minBMI: 27,
    qualifyingComorbidities: [
      "preDiabetes",
      "previousMyocardialInfarction",
      "previousStroke",
      "symptomaticPeripheralArteryDisease",
    ],
  },
];

const hasAnyMedicareBridgeCondition = (
  conditions: string[],
  candidates: string[]
) => candidates.some((condition) => conditions.includes(condition));

// T2DB (type 2 diabetes) and the Medicare Bridge disqualifying conditions
// (OSA, MASH) rule a user out of the Medicare Bridge regardless of BMI. This
// is distinct from simply failing the BMI/comorbidity tiers below with no
// disqualifying condition at all.
export const isDisqualifiedFromMedicareBridgeByCondition = () => {
  const surveyAnswers = getSurveyAnswers();
  const conditions: string[] = surveyAnswers.medicareBridgeConditions ?? [];

  return Boolean(
    surveyAnswers.currentHealthRiskMedical?.includes("type2Diabetes") ||
      hasAnyMedicareBridgeCondition(
        conditions,
        MEDICARE_BRIDGE_DISQUALIFYING_CONDITIONS
      )
  );
};

const isMedicareBridgeScreeningApplicable = () =>
  !isFreemiumHealthRoute() &&
  answeredYesToMedicareBridgeScreening() &&
  isEligibleForBrandedAsync();

const meetsMedicareBridgeBmiCriteria = () => {
  const surveyAnswers = getSurveyAnswers();
  const conditions: string[] = surveyAnswers.medicareBridgeConditions ?? [];
  const bmi = getBMI();

  return MEDICARE_BRIDGE_ELIGIBILITY_TIERS.some(
    ({ minBMI, qualifyingComorbidities }) =>
      bmi >= minBMI &&
      (!qualifyingComorbidities ||
        hasAnyMedicareBridgeCondition(conditions, qualifyingComorbidities))
  );
};

export const isEligibleForMedicareBridge = () =>
  isMedicareBridgeScreeningApplicable() &&
  !isDisqualifiedFromMedicareBridgeByCondition() &&
  meetsMedicareBridgeBmiCriteria();

export const getMedicareBridgeCriteriaEligibility = ():
  | "BRIDGE_ELIGIBLE"
  | "INELIGIBLE_DUE_TO_PART_D_COVERAGE"
  | "INELIGIBLE_DUE_TO_BMI_CONDITIONS"
  | undefined => {
  if (!isMedicareBridgeScreeningApplicable()) {
    return undefined;
  }

  if (isDisqualifiedFromMedicareBridgeByCondition()) {
    return "INELIGIBLE_DUE_TO_PART_D_COVERAGE";
  }

  return meetsMedicareBridgeBmiCriteria()
    ? "BRIDGE_ELIGIBLE"
    : "INELIGIBLE_DUE_TO_BMI_CONDITIONS";
};

export const trackBridgeEligibilityIfDetermined = () => {
  const bridgeCriteriaEligibility = getMedicareBridgeCriteriaEligibility();
  if (bridgeCriteriaEligibility) {
    trackBuyflowEvent("BridgeEligibilityDetermined", {
      bridgeCriteriaEligibility,
    });
    // Keys match the crm-uploader-lambda ATTRIBUTE_MAP entries (MGWL-1412)
    saveToBraze({
      user: {
        hasMedicare: hasMedicareInsurance(),
        medicareBridgeEligibility: bridgeCriteriaEligibility,
      },
    });
  }
};
